import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { z } from "zod";

/** A Guid that carries no identity is absent, not empty. */
const emptyAsUndefined = (guid: Guid | null | undefined): Guid | undefined =>
  guid && !guid.isEmpty ? guid : undefined;

export type AssistantCitationJSON = z.infer<typeof AssistantCitationSchema.AssistantCitationJSONZ>;
export type AssistantCitationParams = z.infer<typeof AssistantCitationSchema.AssistantCitationZ>;

export default class AssistantCitationSchema {
  public static readonly AssistantCitationJSONZ = z.object({
    ordinal: z.number(),
    fileId: z.uuid(),
    fileName: z.string(),
    downloadDocumentId: z.uuid().nullish(),
    sourcePath: z.string().nullish(),
    page: z.number().nullish(),
    nodeId: z.uuid().nullish(),
    matched: z.boolean().nullish(),
    text: z.string().nullish(),
    // Set when the API re-checked this citation on a conversation read back and the reader may
    // no longer see the source, so `text` is empty. Omitted entirely on a live turn, where
    // retrieval has just authorized every citation it returns.
    withheld: z.boolean().nullish(),
    // Retrieval diagnostics. Absent unless the API is running with citation diagnostics
    // enabled: they rank sources against each other and say nothing a user can read as a
    // confidence.
    retrieverScore: z.number().nullish(),
    semanticScore: z.number().nullish(),
  });

  public static readonly AssistantCitationZ = z.object({
    ordinal: z.number(),
    fileId: z.instanceof(Guid),
    fileName: z.string(),
    downloadDocumentId: z.instanceof(Guid).optional(),
    sourcePath: z.string().optional(),
    page: z.number().optional(),
    nodeId: z.instanceof(Guid).optional(),
    matched: z.boolean().optional(),
    text: z.string().optional(),
    withheld: z.boolean().optional(),
    retrieverScore: z.number().optional(),
    semanticScore: z.number().optional(),
  });

  public static validate(values: unknown): AssistantCitationParams {
    return parseOrThrow(AssistantCitationSchema.AssistantCitationZ, values, {
      logError: true,
      schemaName: "AssistantCitationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): AssistantCitationJSON {
    return parseOrThrow(AssistantCitationSchema.AssistantCitationJSONZ, json, {
      logError: true,
      schemaName: "AssistantCitationSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is AssistantCitationParams {
    try {
      AssistantCitationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is AssistantCitationJSON {
    try {
      AssistantCitationSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: AssistantCitationJSON): AssistantCitationParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(AssistantCitationSchema.AssistantCitationJSONZ, json, {
      logError: true,
      schemaName: "AssistantCitationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return AssistantCitationSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: AssistantCitationParams): AssistantCitationJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = AssistantCitationSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return AssistantCitationSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    AssistantCitationSchema.AssistantCitationJSONZ,
    AssistantCitationSchema.AssistantCitationZ,
    {
      decode: (data) => ({
        ordinal: data.ordinal,
        // Decoded with allowEmpty because the API sends the nil GUID rather than omitting
        // the field: CitationResponse.FileId and NodeId are non-nullable Guids, and
        // AuthorizedRagRetrievalService falls back to Guid.Empty for any identifier it
        // cannot parse. Without this, one such citation threw here and took the rendering
        // of the entire answer with it.
        fileId: Guid.fromJSON(data.fileId, { allowEmpty: true }) ?? Guid.EMPTY,
        fileName: data.fileName,
        downloadDocumentId: emptyAsUndefined(Guid.fromJSON(data.downloadDocumentId, { allowEmpty: true })),
        sourcePath: data.sourcePath ?? undefined,
        page: data.page ?? undefined,
        // An empty node id is not an identifier, so it is absent rather than empty. It
        // cannot key a citation or resolve a marker click, and treating it as a value
        // makes two such citations indistinguishable.
        nodeId: emptyAsUndefined(Guid.fromJSON(data.nodeId, { allowEmpty: true })),
        matched: data.matched ?? undefined,
        text: data.text ?? undefined,
        withheld: data.withheld ?? undefined,
        retrieverScore: data.retrieverScore ?? undefined,
        semanticScore: data.semanticScore ?? undefined,
      }),
      encode: (citation) => ({
        ordinal: citation.ordinal,
        fileId: citation.fileId.toJSON(),
        fileName: citation.fileName,
        downloadDocumentId: citation.downloadDocumentId?.toJSON(),
        sourcePath: citation.sourcePath,
        page: citation.page,
        nodeId: citation.nodeId ? citation.nodeId.toJSON() : undefined,
        matched: citation.matched,
        text: citation.text,
        withheld: citation.withheld,
        retrieverScore: citation.retrieverScore,
        semanticScore: citation.semanticScore,
      }),
    }
  );
}
